import React, { useEffect, useState } from 'react';
import CustomInput from '../CustomInput';
import { useSelector } from 'react-redux';
import ErrorContainer from '../ErrorContainer';
import { validateCurrency } from '../../../../client/Juno/helpers';
import AmountWithCurrency from '../../common/AmountWithCurrency';
import { Checkbox } from '@mui/material';
import SelectWithInput from '../../common/SelectWithInput';
import PayOutModal from './PayOutModal';
import useHandleActionClick from '../../../helpers/usehandleActionClick';
import { getMetadata, REQUIRED_FIELDS } from './payoutHelpers';
import JunoButton from '../../common/JunoButton';

const PayOutForm = ({
    isModal = false,
    countries,
    formData,
    formFieldErrors,
    setFormData,
    setFormFieldErrors,
    isLoading,
    transactionFee,
    validateField,
    typeSpecificFields,
    validateAll,
    handleCreate,
    onClose,
    modals,
    setModals,
    type,
    accountSelected,
    selectedAccountReqMissing = [],
    setSelectedAccountData,
}) => {
    const { balancesCurrencies = [] } = useSelector(state => state.juno);
    const [modalAction, setModalAction] = useState(false);
    const balancesData = useSelector(state => state.juno.balancesCurrencies);
    const kycStatus = useSelector(state => state.juno.clientData)?.kyc?.status;
    const handleActionClick = useHandleActionClick(kycStatus, setModalAction);
    const typeSpecificMetadata = getMetadata(type);

    const handleChange = (e, dataKey) => {
        const { name, value } = e.target;
        const target = dataKey || name;
        const processedValue = target === 'fiatAmount' ? value.replace(/[^0-9.]/g, '') : value;

        const error = validateField(
            target,
            processedValue,
            formData.selectedCurrency,
            typeSpecificFields,
        );

        setFormFieldErrors(prev => ({
            ...prev,
            [target]: error,
        }));

        setFormData(prev => ({
            ...prev,
            [target]: processedValue,
        }));
    };

    const handleCurrencyChange = val => {
        setFormData(prev => ({
            ...prev,
            fiatAmount: '',
            selectedCurrency: val,
        }));

        const currencyError = validateCurrency(val?.currencyShortName, balancesData);

        if (currencyError) {
            setFormFieldErrors(prev => ({
                ...prev,
                fiatAmount: '',
                selectedCurrency: currencyError,
            }));
        } else {
            setFormFieldErrors(prev => ({
                ...prev,
                fiatAmount: '',
                selectedCurrency: '',
            }));
        }
    };

    const handleSubmit = e => {
        e.preventDefault();
        const isFormValid = validateAll(typeSpecificFields);

        if (isFormValid) {
            handleActionClick(true);
            setModals(prev => ({ ...prev, openMainModal: true }));
        }
    };

    // Helper function to check if a field is required
    const isFieldRequired = dataKey => REQUIRED_FIELDS.includes(dataKey);

    useEffect(() => {
        if (formFieldErrors) {
            let isError = Object.entries(formFieldErrors).filter(([_, value]) => value);
            if (isError?.[0]?.[0]) {
                let firstError = isError[0][0];
                let el = document.querySelector(`#modal-payout #${firstError}-error`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }
    }, [formFieldErrors]);

    return (
        <div
            id={isModal ? 'modal-payout' : ''}
            className="flex flex-col items-center gap-2.5 self-stretch">
            <form
                aria-label={`${typeSpecificMetadata.heading} Form`}
                className={`w-full mx-auto ${!isModal
                    ? 'rounded-lg h-[444px] border border-[#E4E4E7] dark:!border-0 !p-4 bg-quickforms-bg'
                    : ''
                    }`}>
                <AmountWithCurrency
                    errorMessage={formFieldErrors.fiatAmount || formFieldErrors.selectedCurrency}
                    transactionLabel={typeSpecificMetadata?.amountWithCurrencyLabel}
                    data={balancesCurrencies}
                    amountValue={formData.fiatAmount}
                    selected={formData.selectedCurrency}
                    onAmountChange={handleChange}
                    onCurrencyChange={handleCurrencyChange}
                    isRequired={true}
                    dataE2e="currency-dropdown"
                    errorId="fiatAmount-error"
                />
                {accountSelected ? (
                    <div>
                        <CustomInput
                            label={'account nickname'}
                            value={formData['accountNickName']}
                            disabled={true}
                        />
                        <span className="text-uppercase text-balanceCard-manage text-sm my-3 block">
                            receiver:{' '}
                            <span className="text-text">{formData['beneficiaryName']}</span>
                        </span>
                        <div className="border-t border-[#D1D1D6] -mx-6 dark:border-[#525252] " />
                    </div>
                ) : null}
                {typeSpecificFields
                    .filter(field => {
                        if (accountSelected) {
                            if (field.showOnAccountSelect) return true;
                            if (selectedAccountReqMissing.includes(field.dataKey)) return true;
                            return false;
                        } else {
                            return field.dataKey == 'accountNickName' ? formData.save : true;
                        }
                    })
                    .map(field => {
                        const required = isFieldRequired(field.dataKey);
                        return (
                            <React.Fragment key={`${field.dataKey}-input`}>
                                {field.type == 'select' ? (
                                    <SelectWithInput
                                        className="mt-4 "
                                        labelClassName="!text-sm"
                                        label={field.label}
                                        options={countries.map(country => country.name)}
                                        selected={formData[field.dataKey]}
                                        placeholder={field.placeholder}
                                        value={formData[field.dataKey]}
                                        onChange={(_, selected) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                [field.dataKey]: selected,
                                            }));

                                            setFormFieldErrors(prev => ({
                                                ...prev,
                                                [field.dataKey]: '',
                                            }));
                                        }}
                                        required={required}
                                    />
                                ) : (
                                    <CustomInput
                                        className={field.style || ''}
                                        label={field.label}
                                        type={field.type}
                                        name={field.name}
                                        placeholder={field.placeholder}
                                        value={formData[field.dataKey]}
                                        onChange={event => handleChange(event, field.dataKey)}
                                        aria-invalid={!!formFieldErrors[field.dataKey]}
                                        aria-describedby={
                                            formFieldErrors[field.dataKey]
                                                ? `${field.name}-error`
                                                : undefined
                                        }
                                        disabled={
                                            isLoading ||
                                            (accountSelected && field.dataKey == 'accountNickName')
                                        }
                                        required={field.name == 'accountNickName' ? true : required}
                                    />
                                )}
                                {formFieldErrors[field.dataKey] && (
                                    <ErrorContainer
                                        id={`${field.name}-error`}
                                        message={formFieldErrors[field.dataKey]}
                                    />
                                )}
                                {field.includeSave ? (
                                    <div className="flex gap-2 mt-2 text-[#18181B] uppercase text-xs dark:text-white">
                                        <Checkbox
                                            className="p-0 m-0 text-[#18181B] dark:text-white  "
                                            checked={formData[field.includeSave.dataKey]}
                                            onChange={() => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    [field.includeSave.dataKey]:
                                                        !prev[field.includeSave.dataKey],
                                                }));
                                            }}
                                        />
                                        <span className="self-center">
                                            {field.includeSave.label}
                                        </span>
                                    </div>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
            </form>
            {!isModal ? (
                <JunoButton
                    dataE2e="review-withdraw-btn"
                    type="widget"
                    onClick={handleSubmit}
                    disabled={
                        !formData.fiatAmount ||
                        !formData.beneficiaryName ||
                        formFieldErrors.fiatAmount ||
                        formFieldErrors.beneficiaryName
                    }>
                    REVIEW WITHDRAWAL
                </JunoButton>
            ) : null}
            {modalAction ? (
                <PayOutModal
                    formProps={{
                        formData,
                        formFieldErrors,
                        setFormData,
                        setFormFieldErrors,
                        isLoading,
                        transactionFee,
                        validateField,
                        validateAll,
                        handleCreate,
                        onClose,
                    }}
                    onClose={() => {
                        setModalAction(false);
                        onClose();
                    }}
                    selectedAccountReqMissing={selectedAccountReqMissing}
                    setSelectedAccountData={setSelectedAccountData}
                    type={type}
                    modals={modals}
                    setModals={setModals}
                    openedFromForm={true}
                />
            ) : null}
        </div>
    );
};

export default PayOutForm;
